import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// The associate membership application's letter (TMS-1465). The chosen file goes straight to
// storage (Active Storage direct upload) while the applicant watches its progress, and only its
// signed id rides on the step's form, in `field` — so a slow, failed or cancelled upload never
// costs the applicant the step, and Continue (registration-step) waits until `field` holds one.
//
// - A file of the wrong type or over the size limit is refused before anything is sent.
// - An upload in progress can be cancelled: the letter on file before it, if any, comes back.
// - A failed upload can be retried with the same file, without choosing it again.
// - A finished upload can be replaced (choose another) or removed.
// The server checks the type and size again (AssociateApplicationDetail).
export default class extends Controller {
  static targets = ["field", "input", "dropzone", "item", "name", "status", "bar", "progress",
                    "cancel", "retry", "replace", "remove", "download", "error"]
  static values = { url: String, maxBytes: Number, extensions: Array, types: Array }

  connect() {
    this.file = null
    this.xhr = null
    this.uploadId = 0
    this.saved = { value: this.fieldTarget.value, name: this.nameTarget.textContent,
                   downloadable: this.hasDownloadTarget && !this.downloadTarget.hidden }
  }

  disconnect() {
    this.abort()
  }

  browse(event) {
    event.preventDefault()
    this.inputTarget.click()
  }

  select() {
    const file = this.inputTarget.files[0]
    this.inputTarget.value = ""
    if (file) this.choose(file)
  }

  dragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("drag-over")
  }

  dragLeave() {
    this.dropzoneTarget.classList.remove("drag-over")
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("drag-over")
    const file = event.dataTransfer?.files?.[0]
    if (file) this.choose(file)
  }

  choose(file) {
    const problem = this.problemWith(file)
    if (problem) {
      this.showError(problem)
      return
    }

    this.file = file
    this.start()
  }

  problemWith(file) {
    const name = file.name.toLowerCase()
    const extensionOk = this.extensionsValue.some((extension) => name.endsWith(extension))
    const type = (file.type || "").toLowerCase()
    const typeOk = type === "" || type === "application/octet-stream" || this.typesValue.includes(type)
    if (!extensionOk || !typeOk) return "That file type isn’t accepted. Upload a PDF, JPG, PNG or DOCX file."
    if (file.size > this.maxBytesValue) return "That file is larger than 50 MB. Upload a smaller file."
    return null
  }

  start() {
    this.abort()
    const id = ++this.uploadId
    this.showError(null)
    this.setField("")
    this.render("uploading", this.file.name)
    this.setProgress(0)

    const upload = new DirectUpload(this.file, this.urlValue, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        this.xhr = xhr
        xhr.upload.addEventListener("progress", (event) => {
          if (id === this.uploadId && event.lengthComputable) this.setProgress(event.loaded / event.total)
        })
      }
    })

    upload.create((error, blob) => {
      if (id !== this.uploadId) return
      this.xhr = null

      if (error) {
        this.render("failed", this.file.name)
        this.showError("The upload didn’t finish. Check your connection and try again.")
        return
      }

      this.saved = { value: blob.signed_id, name: this.file.name, downloadable: false }
      this.setField(blob.signed_id)
      this.render("done", this.file.name, { downloadable: false })
    })
  }

  // Stops the upload in progress and goes back to the letter on file before it, if any.
  cancel(event) {
    event.preventDefault()
    this.abort()
    this.showError(null)
    this.restoreSaved()
  }

  retry(event) {
    event.preventDefault()
    if (this.file) this.start()
  }

  replace(event) {
    this.browse(event)
  }

  // Removes the letter, or after a failed upload discards that attempt.
  remove(event) {
    event.preventDefault()
    const failed = this.element.dataset.state === "failed"
    this.abort()
    this.file = null
    if (!failed) this.saved = { value: "", name: "", downloadable: false }
    this.showError(null)
    this.restoreSaved()
  }

  restoreSaved() {
    this.setField(this.saved.value)
    if (this.saved.value) {
      this.render("done", this.saved.name, { downloadable: this.saved.downloadable })
    } else {
      this.render("empty")
    }
  }

  abort() {
    this.uploadId++
    if (this.xhr) this.xhr.abort()
    this.xhr = null
  }

  render(state, name = "", { downloadable = false } = {}) {
    this.element.dataset.state = state
    this.dropzoneTarget.hidden = state !== "empty"
    this.itemTarget.hidden = state === "empty"
    this.nameTarget.textContent = name
    this.statusTarget.className = `file-status ${state === "done" ? "done" : state === "failed" ? "failed" : "uploading"}`
    this.statusTarget.textContent = { uploading: "Uploading…", failed: "Upload failed", done: "Uploaded" }[state] || ""
    this.barTarget.hidden = state !== "uploading"
    this.cancelTarget.hidden = state !== "uploading"
    this.retryTarget.hidden = state !== "failed"
    this.replaceTarget.hidden = state !== "done"
    this.removeTarget.hidden = state !== "done" && state !== "failed"
    if (this.hasDownloadTarget) this.downloadTarget.hidden = !(state === "done" && downloadable)
  }

  setProgress(fraction) {
    const percent = Math.round(fraction * 100)
    this.progressTarget.style.width = `${percent}%`
    this.barTarget.setAttribute("aria-valuenow", percent)
    if (this.element.dataset.state === "uploading") this.statusTarget.textContent = `Uploading… ${percent}%`
  }

  // Assigning a value fires no event, so the form's Continue (registration-step) is told here.
  setField(value) {
    this.fieldTarget.value = value
    this.fieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  showError(message) {
    this.errorTarget.textContent = message || ""
    this.errorTarget.hidden = !message
  }
}
